// Pure, DOM-free logic behind RecipientCombobox. It lives here rather than in the component because jest
// in this repo is node-only (jest.config.js testEnvironment "node", testMatch "**/*.test.ts" — no jsdom,
// no @testing-library), so the load-bearing rules — especially "what's shown is what's sent" — are pinned
// as unit tests here; the DOM wiring (focus, click-outside, virtual keyboard, a11y) is covered by the
// Playwright Summary spec. Mirrors the consentLogic.ts split.

import { RecipientOption, filterRecipients } from "./summaryExits";

export { filterRecipients };

// Move the keyboard highlight within a list of `len` options. An empty list has no highlight (-1), so a
// query that matches nobody cannot commit a selection (see selectHighlighted). From "no highlight" (-1),
// Down goes to the first option and Up to the last; otherwise it wraps.
export const moveHighlight = (index: number, len: number, delta: number): number => {
    if (len === 0) {
        return -1;
    }
    if (index < 0) {
        return delta > 0 ? 0 : len - 1;
    }
    return (index + delta + len) % len;
};

// The option a commit (Enter / option click) selects, or null when there is nothing to commit — an empty
// filtered list or no active highlight. Never invents a selection the query excluded: an out-of-range or
// negative highlight yields null and the caller must leave the current selection untouched.
export const selectHighlighted = (options: RecipientOption[], highlight: number): RecipientOption | null => {
    if (highlight < 0 || highlight >= options.length) {
        return null;
    }
    return options[highlight];
};

// What actually gets sent: the committed selection if it is still a real recipient, else the first
// recipient, else undefined (no recipients — the caller's `!recipient` guard blocks the send). It
// deliberately ignores the in-progress query: a typed filter never changes who the mail goes to, only an
// explicit selection does. This is the "what's shown is what's sent" guarantee — the combobox displays
// this same value when closed.
export const effectiveRecipient = (
    active: RecipientOption | undefined,
    recipients: RecipientOption[]
): RecipientOption | undefined => {
    if (active && recipients.some(r => r.id === active.id)) {
        return active;
    }
    return recipients[0];
};
